// Messages: the calls a person's own inbox on the web makes.
//
// These are the routes the app uses (/social-dm/...), with the same rules. The server
// names the caller from the sign-in token alone, checks the person's tick for the
// project (a super admin has Messages in every project), and answers one plain 404 to
// anyone it will not serve. Nothing here is a second copy of a conversation, and
// nothing here can show anyone else's.
//
// Every call is quiet (`skipSessionExpiry`), for the reason the search calls are (see
// ./searchApi). These routes answer 401 to a person who IS signed in whenever the
// server cannot name them from the token, and it remembers such a miss for a minute.
// Asked plainly, that would put the Session Expired dialog in front of someone who only
// had the page open. So a refusal here means one thing to the caller: this could not be
// shown.
//
// What is sent is spelled out here and nowhere else. The project travels only where the
// route takes one: the two lists, the unread count, the privacy notice, the people
// pickers, opening a chat and making a group. An open conversation (and a group being
// managed) is named by its own id, and the server reads its project from it, never from
// the request.
//
// Starting a one-to-one chat and creating a group are here too, with the app's routes
// and bodies (apps/mobile/src/services/socialDmApi.ts). The people offered come from the
// server, which decides who may be messaged or added; nothing here picks or filters.
//
// Managing a group is here too, with the app's routes and bodies: reading a group's
// people, renaming it, adding and removing people (a group admin's), and leaving it
// (anyone's). The server decides who may do each, from the group itself; the page
// draws a control only where the group's own answer says the person is an admin.
//
// Left out on purpose in this version: making someone an admin, sending a photo or a
// voice note, mute, block and report. The app has them.
import api from './api';

/** The other person in a one-to-one chat. The list sends the first three; an open chat adds the rest. */
export interface MessagePerson {
  empCode: string;
  name: string;
  designation: string | null;
  storeName?: string | null;
  photo?: string | null;
}

/** One one-to-one chat in the list. */
export interface ChatRow {
  conversationId: string;
  projectId: number;
  other: MessagePerson;
  /** At most 140 characters. A photo or voice note with no words has a short label instead. */
  lastMessageSnippet: string | null;
  /** The employee code of whoever sent the last message. */
  lastMessageSender: string | null;
  /** One counter across every conversation, so chats and groups sort together on it. */
  lastMessageSeq: number;
  /** Epoch milliseconds. */
  lastMessageAt: number | null;
  unreadCount: number;
  muted: boolean;
  myLastReadSeq: number;
  otherLastReadSeq: number;
  otherDeliveredSeq: number;
}

export type GroupRole = 'admin' | 'member';

/** One group in the list. */
export interface GroupRow {
  groupId: string;
  projectId: number;
  title: string;
  /** People in the group now. Someone who left is not counted. */
  memberCount: number;
  myRole: GroupRole;
  lastMessageSnippet: string | null;
  /** Null when the last line is one the server wrote ("A added B"), which nobody sent. */
  lastMessageSender: { empCode: string; name: string } | null;
  lastMessageSeq: number;
  lastMessageAt: number | null;
  unreadCount: number;
  muted: boolean;
  myLastReadSeq: number;
}

/**
 * A photo or a voice note on a message. `path` and `thumbnailPath` are addresses under
 * the API, already signed for this viewer and good for six hours, so a plain <img> or
 * <audio> can open them with no sign-in header (see messageMediaUrl).
 */
export interface MessageMedia {
  kind: 'image' | 'voice';
  mime: string | null;
  bytes: number | null;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  path: string | null;
  thumbnailPath: string | null;
}

/** 'system' is a line the server wrote in a group. Its whole text is in `content`. */
export type MessageKind = 'text' | 'image' | 'voice' | 'system';

/**
 * One message. A removed message keeps its place and loses its words and its
 * attachment: `content` is null, `kind` is 'text' and `media` is null.
 */
export interface Message {
  id: string;
  seq: number;
  /** Lower case, as the server keeps employee codes. */
  senderEmpCode: string;
  senderName: string;
  content: string | null;
  kind: MessageKind;
  media: MessageMedia | null;
  removed: boolean;
  /** Epoch milliseconds. */
  createdAt: number | null;
}

/**
 * The answer to a send: the message as stored, with the id it was sent under.
 * `seq` is null in one case only: the server took the message and stored nothing. It
 * answers that way on purpose and the page shows the message as sent, as the app does.
 */
export interface SentMessage extends Omit<Message, 'seq' | 'senderName'> {
  seq: number | null;
  senderName?: string;
  clientMsgId: string;
}

export interface ChatList {
  success: true;
  data: ChatRow[];
  /** One-to-one unread over ALL the person's projects. The page does not show it: see `unread`. */
  totalUnread: number;
  nextBeforeSeq: number | null;
}

export interface GroupList {
  success: true;
  data: GroupRow[];
  /** Group unread for this project only. */
  totalUnread: number;
  nextBeforeSeq: number | null;
}

/**
 * An open one-to-one chat, newest message first. While the privacy notice is not
 * accepted the server sends no messages: `requiresDisclosure` is true, `version` says
 * which notice, and the fields after it are absent.
 */
export interface ChatThread {
  success: true;
  data: Message[];
  other: MessagePerson;
  /** The project the chat is in. An older server does not send it. */
  projectId?: number;
  requiresDisclosure: boolean;
  version?: number;
  lastReadSeq?: number;
  otherLastReadSeq?: number;
  otherDeliveredSeq?: number;
  unreadCount?: number;
  muted?: boolean;
  nextBeforeSeq?: number | null;
}

/** An open group, newest message first: only what was sent after this person joined. */
export interface GroupThread {
  success: true;
  data: Message[];
  group: { groupId: string; projectId: number; title: string; memberCount: number; myRole: GroupRole };
  requiresDisclosure: boolean;
  version?: number;
  lastReadSeq: number;
  unreadCount: number;
  muted: boolean;
  nextBeforeSeq: number | null;
}

/** `duplicate` is true when this id had been stored already: the message is the stored one. */
export interface SendAnswer {
  success: true;
  data: SentMessage;
  duplicate?: boolean;
}

/** The person's unread in one project. A side whose tick they do not hold counts 0. */
export interface UnreadCount {
  direct: number;
  groups: number;
  total: number;
}

/** The longest message the server takes, one-to-one or in a group. */
export const MAX_MESSAGE_CHARS = 2000;

/**
 * Someone the two people pickers offer: for a one-to-one chat, or to add to a new group.
 * The same shape from both routes.
 */
export interface PickPerson {
  empCode: string;
  name: string;
  designation: string | null;
  storeName: string | null;
  photo: string | null;
}

/** The answer to "open a chat with this person": the chat, new or the one there already. */
export interface OpenedChat {
  conversationId: string;
  projectId: number;
  other: PickPerson;
  /** The person has not accepted the privacy notice in this project. The pane shows it first. */
  disclosureRequired: boolean;
  disclosureVersion: number;
}

/** A group just made, as the server describes it. Only what the page reads is named. */
export interface CreatedGroup {
  groupId: string;
  projectId: number;
  title: string;
  memberCount: number;
  disclosureRequired?: boolean;
}

/** One person in a group, as the group's own answer lists them. `photo` is always null there. */
export interface GroupMember extends PickPerson {
  role: GroupRole;
}

/**
 * A group as its own route answers (GET /social-dm/groups/:groupId), and as rename, add
 * and remove answer with it afterwards. Admins first, then by name. `myRole` is the
 * caller's role in this group: the only thing the page goes by to draw an admin's
 * controls.
 */
export interface GroupDetail {
  groupId: string;
  projectId: number;
  title: string;
  createdBy: string;
  myRole: GroupRole;
  muted: boolean;
  memberCount: number;
  members: GroupMember[];
  disclosureRequired: boolean;
  disclosureVersion: number;
}

/** `duplicate` is true when this client id had made a group already: the group is that one. */
export interface CreateGroupAnswer {
  success: true;
  data: CreatedGroup;
  duplicate?: boolean;
}

/**
 * The server's group limits (MAX_DM_GROUP_TITLE_CHARS, MIN_DM_GROUP_MEMBERS and
 * MAX_DM_GROUP_MEMBERS in apps/api/src/services/social-dm.service.ts). The people
 * counts include the person making the group. The server checks every one again.
 */
export const GROUP_LIMITS = { TITLE_CHARS: 60, MIN_PEOPLE: 2, MAX_PEOPLE: 50 } as const;

/** Fewer letters than this and the pickers list the person's own team instead of searching. */
export const MIN_FIND_CHARS = 2;

/**
 * What groupCreateCheck sends as the words: one character. Not empty (that is the
 * team, looked up in full) and too short to search, so the group picker makes all its
 * checks and then answers with nobody, without looking anyone up
 * (searchGroupDirectory in apps/api/src/controllers/social-dm-group.controller.ts).
 */
export const GROUP_CHECK_WORDS = '.';

type Envelope<T> = { success: boolean; data: T };
type QuietConfig = NonNullable<Parameters<typeof api.get>[1]> & { skipSessionExpiry: true };
const quiet = (params?: Record<string, unknown>, signal?: AbortSignal): QuietConfig => ({ params, signal, skipSessionExpiry: true });
/** The older page of a list or a conversation: everything before this number. Nothing is sent for the newest page. */
const before = (beforeSeq?: number | null): { beforeSeq: number } | undefined => (beforeSeq != null ? { beforeSeq } : undefined);
const chat = (conversationId: string): string => `/social-dm/conversations/${encodeURIComponent(conversationId)}`;
const group = (groupId: string): string => `/social-dm/groups/${encodeURIComponent(groupId)}`;

export const messagesAPI = {
  /** How much is unread in this project. It reads only: nothing is marked read or delivered. */
  unread: (projectId: number, signal?: AbortSignal) =>
    api.get<Envelope<UnreadCount>>('/social-dm/unread', quiet({ projectId }, signal)),

  /** The person's one-to-one chats in this project, newest first, 30 to a page. */
  chats: (projectId: number, beforeSeq?: number | null, signal?: AbortSignal) =>
    api.get<ChatList>('/social-dm/conversations', quiet({ projectId, ...before(beforeSeq) }, signal)),
  /**
   * The groups the person is in, in this project, newest first, 30 to a page.
   *
   * `web: 1` tells the server this is the browser. The app's call to this route is how
   * the server learns that a person's phone can open groups, and it sends group pushes
   * only then. A browser reading the list must not count as that.
   */
  groups: (projectId: number, beforeSeq?: number | null, signal?: AbortSignal) =>
    api.get<GroupList>('/social-dm/groups', quiet({ projectId, web: 1, ...before(beforeSeq) }, signal)),

  /** The newest 40 messages, or the 40 before `beforeSeq`. */
  chatMessages: (conversationId: string, beforeSeq?: number | null, signal?: AbortSignal) =>
    api.get<ChatThread>(`${chat(conversationId)}/messages`, quiet(before(beforeSeq), signal)),
  groupMessages: (groupId: string, beforeSeq?: number | null, signal?: AbortSignal) =>
    api.get<GroupThread>(`${group(groupId)}/messages`, quiet(before(beforeSeq), signal)),

  /** Text only. `clientMsgId` comes from newClientMsgId, once per message: see there. */
  sendToChat: (conversationId: string, content: string, clientMsgId: string) =>
    api.post<SendAnswer>(`${chat(conversationId)}/messages`, { content, clientMsgId }, quiet()),
  sendToGroup: (groupId: string, content: string, clientMsgId: string) =>
    api.post<SendAnswer>(`${group(groupId)}/messages`, { content, clientMsgId }, quiet()),

  /** "I have read up to here." Shared with the phone: its count for this conversation drops too. */
  markChatRead: (conversationId: string, lastReadSeq: number) =>
    api.post<Envelope<{ lastReadSeq: number; unreadCount: number }>>(`${chat(conversationId)}/read`, { lastReadSeq }, quiet()),
  markGroupRead: (groupId: string, lastReadSeq: number) =>
    api.post<Envelope<{ lastReadSeq: number; unreadCount: number; totalUnread: number }>>(`${group(groupId)}/read`, { lastReadSeq }, quiet()),

  /** Records that the person accepted the privacy notice for this project. The server decides which version. */
  acceptNotice: (projectId: number) =>
    api.post<Envelope<{ version: number }>>('/social-dm/disclosure/ack', { projectId }, quiet()),

  /**
   * People to start a one-to-one chat with. An empty `q` is the person's own team;
   * two letters or more searches the whole project. Refused (404) for a person without
   * Direct Messages here: the same checks as the chat list and as opening a chat.
   */
  people: (projectId: number, q: string, signal?: AbortSignal) =>
    api.get<Envelope<PickPerson[]>>('/social-dm/directory/search', quiet({ projectId, q }, signal)),
  /**
   * Open the one-to-one chat with this person, making it if there is none. Asked twice,
   * it answers with the same chat. Refused (404) for anyone the person may not message.
   */
  openChat: (projectId: number, targetEmpCode: string) =>
    api.post<Envelope<OpenedChat>>('/social-dm/conversations', { projectId, targetEmpCode }, quiet()),

  /**
   * People who can be added to a new group: only those whose role has Group Chats. An
   * empty `q` is the person's own team. Refused (404) unless the person holds Group
   * Chats AND Create Groups here: the same checks as creating a group.
   */
  groupPeople: (projectId: number, q: string, signal?: AbortSignal) =>
    api.get<Envelope<PickPerson[]>>('/social-dm/groups/directory', quiet({ projectId, q }, signal)),
  /**
   * May this person create a group here? The group picker asked with GROUP_CHECK_WORDS:
   * the checks creating a group makes (in the project or a validated super admin, Group
   * Chats, Create Groups), then an empty list, or the plain 404. Nobody is looked up for
   * it, so the page can ask it on every opening without waiting on the team lookup.
   */
  groupCreateCheck: (projectId: number) =>
    api.get<Envelope<PickPerson[]>>('/social-dm/groups/directory', quiet({ projectId, q: GROUP_CHECK_WORDS })),
  /**
   * Make a group. `memberEmpCodes` is the OTHER people: the server adds the person
   * making it, as its admin. `clientGroupId` comes from newClientMsgId, once per
   * "New group" pop-up, and goes with every try: the server answers a second try with
   * the group the first one made, so a double click or a retry never makes two.
   */
  createGroup: (projectId: number, title: string, memberEmpCodes: string[], clientGroupId: string) =>
    api.post<CreateGroupAnswer>('/social-dm/groups', { projectId, title, memberEmpCodes, clientGroupId }, quiet()),

  /**
   * A group's people and the caller's role in it. The plain 404 for anyone not in it now
   * (left, removed, or the Group Chats tick gone).
   */
  groupInfo: (groupId: string, signal?: AbortSignal) =>
    api.get<Envelope<GroupDetail>>(group(groupId), quiet(undefined, signal)),
  /** A group admin's. The server keeps the name as cleanTitle does, and answers with the group. */
  renameGroup: (groupId: string, title: string) =>
    api.patch<Envelope<GroupDetail>>(group(groupId), { title }, quiet()),
  /**
   * People an admin can add to this group: the group picker route with the group's id,
   * which leaves out everyone in the group now. Refused (404) unless the person is an
   * admin of the group, in its project, with Group Chats.
   */
  peopleToAdd: (projectId: number, groupId: string, q: string, signal?: AbortSignal) =>
    api.get<Envelope<PickPerson[]>>('/social-dm/groups/directory', quiet({ projectId, groupId, q }, signal)),
  /** A group admin's. `empCodes` is the people to add; someone in the group already is skipped. */
  addToGroup: (groupId: string, empCodes: string[]) =>
    api.post<Envelope<GroupDetail>>(`${group(groupId)}/members`, { empCodes }, quiet()),
  /** A group admin's, for anyone but themselves (that is leaveGroup). */
  removeFromGroup: (groupId: string, empCode: string) =>
    api.delete<Envelope<GroupDetail>>(`${group(groupId)}/members/${encodeURIComponent(empCode)}`, quiet()),
  /**
   * Anyone's, always. If the last admin leaves, the person in the group longest becomes
   * admin, and the server says so in the group's own lines. No body, as the app sends none.
   */
  leaveGroup: (groupId: string) =>
    api.post<{ success: boolean }>(`${group(groupId)}/leave`, undefined, quiet()),
};

/**
 * A fresh id for one message (or for one new group: see createGroup). The server keeps
 * one message per id in a conversation, so the same message sent again under the same
 * id (after a timeout, or a second press) is answered with the one already stored and
 * is never posted twice. Make one per message, and keep it for every try of that message.
 */
export const newClientMsgId = (): string => {
  const source = (globalThis as { crypto?: Crypto }).crypto;
  if (source?.randomUUID) return source.randomUUID();
  // A page not served over https has no randomUUID. The server takes any id of this shape.
  const bytes = new Uint8Array(16);
  if (source?.getRandomValues) source.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/**
 * The address of a photo or a voice note, for a plain <img> or <audio>. The server's
 * path is under the API and carries its own pass, so no sign-in header is needed and
 * none is sent. Null when the message has nothing stored to open.
 */
export const messageMediaUrl = (path: string | null | undefined): string | null => {
  if (!path) return null;
  const base = String(api.defaults.baseURL ?? '').replace(/\/+$/, '');
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
};
